import { useLingui } from '@lingui/react/macro';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useCallback } from 'react';
import { useDialogStore } from '@/components/dialogs';
import { PipButton } from '../../features/picture_in_picture';
import { CustomConnectButton as ConnectButton } from '../rainbow/CustomConnectButton.tsx';
import { DESKTOP_BREAKPOINT, MOBILE_BREAKPOINT } from './constants';
import { FundAccountMenu } from './fund_account_menu/index.tsx';
import { HeaderActionDivider } from './header_action_divider.tsx';
import { headerActionsGap } from './header_action_styles.ts';
import { LanguageMenuButton } from './language_menu.tsx';
import { NotificationsButton } from './notifications_button.tsx';
import { PointsButton } from './points_button.tsx';

const DesktopActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    display: 'none',
  },
}));

const PrimaryActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(headerActionsGap),
}));

const UtilityActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const desktopOnlySx = {
  display: { xs: 'none', [DESKTOP_BREAKPOINT]: 'inline-flex' },
};

interface DesktopActionsProps {
  canDeposit: boolean;
  canWithdraw: boolean;
  canBridge: boolean;
  depositButtonText: string;
  showLanguageFeature: boolean;
  showPipFeature: boolean;
  onSettingsMenuOpen: () => void;
  onNotificationsOpen: () => void;
  unreadCount: number;
}

export const DesktopActions = memo(function DesktopActions({
  canDeposit,
  canWithdraw,
  canBridge,
  depositButtonText,
  showLanguageFeature,
  showPipFeature,
  onSettingsMenuOpen,
  onNotificationsOpen,
  unreadCount,
}: DesktopActionsProps) {
  const { t } = useLingui();
  const openDialog = useDialogStore((state) => state.openDialog);

  const handleFundDeposit = useCallback(() => openDialog('depositCollateralDialog'), [openDialog]);
  const handleFundWithdraw = useCallback(() => openDialog('withdrawDialog'), [openDialog]);

  return (
    <DesktopActionsContainer>
      <PrimaryActions>
        <PointsButton sx={desktopOnlySx} />
        {canDeposit ? (
          <>
            <FundAccountMenu
              sx={desktopOnlySx}
              canBridge={canBridge}
              depositLabel={depositButtonText}
              canWithdraw={canWithdraw}
              onDeposit={handleFundDeposit}
              onWithdraw={handleFundWithdraw}
            />
            <HeaderActionDivider sx={desktopOnlySx} />
          </>
        ) : null}

        <ConnectButton accountMenu={{ canWithdraw }} size="medium" />
      </PrimaryActions>

      <UtilityActions>
        <NotificationsButton
          unreadCount={unreadCount}
          onClick={onNotificationsOpen}
          aria-label={t({ comment: 'Header notifications button', message: 'Notifications' })}
        />

        {showLanguageFeature ? <LanguageMenuButton /> : null}

        {showPipFeature ? <PipButton /> : null}

        <IconButton onClick={onSettingsMenuOpen} aria-label={t({ comment: 'Header settings button', message: 'Settings' })}>
          <SettingsIcon />
        </IconButton>
      </UtilityActions>

      {/* <ThemeToggleButton /> */}
    </DesktopActionsContainer>
  );
});
